/**
 * The host the marketing site's scanner carries into signup (task 293).
 *
 * `/tools/site-scanner/` links to `/signup?site=<host>`. Descope's flow
 * redirects for social login and the query is gone by the time onboarding
 * renders, so the host is kept in `sessionStorage`: same origin, so it
 * survives those redirects, and gone when the tab closes. It is validated as
 * a hostname here and again by the site form, is never rendered unescaped,
 * and is never trusted beyond a pre-filled input.
 */
import { normalizeDomain, validateSiteForm } from "@/lib/validation/site";

const KEY = "onboarding:prefill-site";
const SOURCE_KEY = "onboarding:signup-source";

/**
 * Where a signup came from (task 299), kept alongside the host and sent with
 * the provisioning call.
 *
 * A literal chosen here, never the host and never anything from the query
 * string: it ends up in `tenant_lifecycle_events.metadata`, whose standing
 * rule is that it holds no PII, and the API refuses any value outside its own
 * enum anyway. `scan` is the marketing site's site scanner, which is the only
 * thing that links to `/signup?site=`.
 */
export type SignupSource = "scan";

function storage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    // Storage can throw in a private window or with site data blocked; the
    // pre-fill is a convenience and the form works without it.
    return undefined;
  }
}

/** A `?site=` value as a hostname the site form accepts, or undefined. */
export function hostnameFromQuery(search: string): string | undefined {
  const raw = new URLSearchParams(search).get("site");
  if (!raw || raw.length > 253) return undefined;
  const domain = normalizeDomain(raw);
  return validateSiteForm({ name: "site", domain }).domain ? undefined : domain;
}

/**
 * Remember the host a signup link carried, if it carried a valid one, and
 * that the visitor arrived from the scanner.
 *
 * The source is recorded from the *presence* of a valid `?site=`, not from a
 * parameter of its own. Nothing else on the marketing site links to
 * `/signup?site=`, and deriving it here means a hand-typed `?source=` cannot
 * label a signup as something it was not.
 */
export function capturePrefilledSite(search: string): void {
  const host = hostnameFromQuery(search);
  if (!host) return;
  const store = storage();
  store?.setItem(KEY, host);
  store?.setItem(SOURCE_KEY, "scan" satisfies SignupSource);
}

/**
 * The remembered source, if any.
 *
 * Deliberately not cleared when the site is created, the way the host is: by
 * then provisioning has long since happened, and the server reads this only on
 * the call that creates a tenant. It goes when the tab does.
 *
 * Re-checked against the one value we write, for the same reason
 * `readPrefilledSite` re-validates: session storage is writable by any script
 * on this origin, and this one is bound for a table the funnel is read from.
 */
export function readSignupSource(): SignupSource | undefined {
  return storage()?.getItem(SOURCE_KEY) === "scan" ? "scan" : undefined;
}

/** The remembered host, if any. Left in place until the site is created. */
export function readPrefilledSite(): string | undefined {
  const value = storage()?.getItem(KEY) ?? undefined;
  // Re-validated on the way out: storage is writable by any script on the
  // origin, and the form is the only thing that should ever see it.
  return value && !validateSiteForm({ name: "site", domain: value }).domain
    ? value
    : undefined;
}

export function clearPrefilledSite(): void {
  storage()?.removeItem(KEY);
}
